import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils';

export type StepperState = 'not-started' | 'current' | 'complete' | 'blocked';

export interface StepperStep {
  id: string;
  label: string;
  state?: Exclude<StepperState, 'current'>;
}

export interface StepperProps {
  steps: StepperStep[];
  currentStepIndex: number;
  label?: string;
  className?: string;
}

const stateLabels: Record<StepperState, string> = {
  'not-started': 'Not started',
  current: 'Current',
  complete: 'Complete',
  blocked: 'Blocked',
};
const stateClasses: Record<StepperState, string> = {
  'not-started': 'border-control-border bg-surface-sheet text-text-muted',
  current: 'border-action bg-action text-action-foreground',
  complete: 'border-status-success-border bg-status-success-bg text-status-success-fg',
  blocked: 'border-status-warning-border bg-status-warning-bg text-status-warning-fg',
};

/** Presentation only: step order, gating and navigation remain with the journey. */
export function Stepper({ steps, currentStepIndex, label = 'Progress', className }: StepperProps) {
  const current = steps[currentStepIndex];
  if (!current) return null;
  const summary = `Step ${currentStepIndex + 1} of ${steps.length}: ${current.label}`;
  return (
    <div role="group" aria-label={label} className={cn('min-w-0 font-body text-body', className)}>
      <div className="space-y-2 md:hidden">
        <p className="text-text-heading">{summary}</p>
        {current.state && current.state !== 'not-started' && (
          <p className="text-text-muted">{stateLabels[current.state]}</p>
        )}
        {steps[currentStepIndex + 1] && (
          <p className="text-text-muted">Next: {steps[currentStepIndex + 1]?.label}</p>
        )}
        <progress
          aria-label={label}
          aria-valuetext={summary}
          value={currentStepIndex + 1}
          max={steps.length}
          className="sr-only"
        />
        <div aria-hidden="true" className="flex gap-1" data-step-segments>
          {steps.map((step, index) => (
            <span
              key={step.id}
              className={cn(
                'h-1 flex-1 rounded-control border border-border',
                index <= currentStepIndex ? 'bg-action' : 'bg-surface-soft',
              )}
            />
          ))}
        </div>
      </div>
      <ol aria-label={label} className="hidden min-w-0 w-full items-start py-2 md:flex">
        {steps.map((step, index) => {
          const state = step.state ?? (index === currentStepIndex ? 'current' : 'not-started');
          return (
            <li
              key={step.id}
              aria-current={index === currentStepIndex ? 'step' : undefined}
              data-step-state={state}
              className="relative flex min-w-0 flex-1 flex-col items-center gap-2 px-2 text-center text-text-heading"
            >
              {index < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  data-step-connector
                  className="absolute left-1/2 top-4 h-px w-full bg-control-border"
                />
              )}
              <span
                aria-hidden="true"
                data-step-marker
                className={cn(
                  'relative z-10 inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 font-semibold tabular-nums',
                  stateClasses[state],
                )}
              >
                {state === 'complete' ? <Check className="size-4" /> : index + 1}
              </span>
              <div className="min-w-0">
                <span className="break-words font-medium [overflow-wrap:anywhere]">
                  {step.label}
                </span>
                {state !== 'not-started' && (
                  <span className="block text-caption">{stateLabels[state]}</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
